import { apiClient } from './client';
import { validateRedirect } from '@/lib/validateRedirect';
import { parseResponse } from '@/lib/parseResponse';
import { paginatedResponse } from './schemas';
import { z } from 'zod';
import { AUTH_ME_KEY } from '@/auth/authData';
import type {
  User,
  UserContext,
  UserMergeSuggestion,
  NewAppConfig,
  OrganizationListItem,
  NetworkListItem,
  MemberNetwork,
  ContextSwitchInput,
} from '@/types';

// Request/Response types
export interface LoginRequest {
  username: string;
  password: string;
}

/** Successful login: the credentials minted a session token. */
export interface LoginResponse {
  authToken: string;
}

/**
 * #308 (AUTH-PARITY sprint, chunk F) — vingapi's login algorithm now returns
 * this instead of a token when the typed credentials are correct but the
 * matched contact method is not verified FOR THIS USER (or the account is
 * not yet active): a fresh code has already been sent to `verifyEmailOrPhone`
 * and `verificationKey` is the same opaque id `/auth/verify/` expects. No
 * `id`/`username` — the caller is not authenticated yet.
 */
export interface VerificationRequiredResponse {
  status: 'verification_required';
  /** The address/number the fresh code was just sent to. */
  verifyEmailOrPhone: string;
  /** Opaque id submitted to `/auth/verify/` alongside the code. */
  verificationKey: string;
}

/**
 * What `login()` resolves to: a token, or `verification_required`. The two
 * shapes vingapi's login answers (vingapi `src/auth/schemas.py`); anything
 * else is rejected by `isValidLoginResult` (#1175).
 */
export type LoginResult = LoginResponse | VerificationRequiredResponse;

export function isVerificationRequired(
  result: LoginResult
): result is VerificationRequiredResponse {
  return (result as VerificationRequiredResponse).status === 'verification_required';
}

/**
 * Raised when the login endpoint returns 200 with a body that is neither an
 * auth token nor a `verification_required` status. The transport layer (`HtmlResponseError`)
 * already rejects the SPA-fallback HTML case; this is the last line of defense
 * for a malformed-but-parseable JSON 200 — without it, `SignIn` would
 * destructure `authToken` as `undefined` and march into a tokenless
 * "authenticated" state.
 */
export class InvalidLoginResponseError extends Error {
  constructor() {
    super('Login response was not a recognized shape (no auth token, no verification_required status).');
    this.name = 'InvalidLoginResponseError';
  }
}

function isValidLoginResult(result: unknown): result is LoginResult {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  if (r.status === 'verification_required') {
    return typeof r.verifyEmailOrPhone === 'string' && typeof r.verificationKey === 'string';
  }
  return typeof r.authToken === 'string' && r.authToken.length > 0;
}

export interface RegisterRequest {
  /** Exactly one of email/phone — vingapi RegisterInput has no `username`
   *  field (#143: sending `username` 400s every signup). */
  email?: string;
  phone?: string;
  firstName: string;
  lastName: string;
  password: string;
  /** Cloudflare Turnstile token; the client interceptor sends it as
   *  `turnstile_token`. Present only when the signup widget is enabled. */
  turnstileToken?: string;
}

export interface RegisterResponse {
  verificationKey: string;
}

export interface VerifyRequest {
  email?: string;
  phone?: string;
  verificationKey: string;
  verificationCode: string;
}

export interface GenerateLinkRequest {
  email?: string;
  phone?: string;
  reset: boolean;
  /**
   * #1111 / vingapi#1653: the course (`/view/…`) to return to after the mailed
   * or texted link signs the person in. Optional; the server carries it on the
   * link only when it is a safe `/view/` path and silently ignores anything
   * else. Callers send `linkRequestRedirect(searchParams)` and omit it otherwise.
   */
  redirect?: string;
}

export interface ChangePasswordRequest {
  currentPassword?: string;
  newPassword: string;
  reNewPassword: string;
}

/**
 * The /auth/me merge-suggestion shape. Canonical definition lives on the
 * User type (types/user.ts) since the stored user carries it; this alias
 * keeps existing `import { MergeSuggestion } from '@/api/auth'` sites valid.
 */
export type MergeSuggestion = UserMergeSuggestion;

/** @deprecated `mergeSuggestion` now lives on User itself. */
export type AuthMeResponse = User;

// API Functions

/**
 * Login with username and password.
 *
 * Returns `LoginResult` ({authToken} or `verification_required`) so callers
 * switch on the response shape with `isVerificationRequired`.
 */
export async function login(data: LoginRequest): Promise<LoginResult> {
  const response = await apiClient.post<LoginResult>('/api/auth/login/', data);
  // Fail loudly if the 200 body isn't a recognized login shape — a tokenless
  // success must never reach the SignIn flow (see InvalidLoginResponseError).
  if (!isValidLoginResult(response.data)) {
    throw new InvalidLoginResponseError();
  }
  return response.data;
}

/**
 * Register a new user account
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>('/api/auth/register/', data);
  return response.data;
}

/**
 * Verify registration or a login challenge with the code sent via email/SMS.
 *
 * #308: with a `verificationKey`, a correct code returns `{authToken}`
 * whenever the code is right (activation or not) — the person just proved
 * both password and method, so vingapi mints a token directly. A response
 * with no `authToken` (204, or any other token-less 200) means the method
 * was verified but no session was minted here; the caller must send the
 * person to sign in explicitly. `undefined` models both.
 */
export async function verify(data: VerifyRequest): Promise<LoginResponse | undefined> {
  const response = await apiClient.post<LoginResponse | undefined>('/api/auth/verify/', data);
  if (!response.data || !response.data.authToken) return undefined;
  return response.data;
}

/**
 * Generate a magic link or password reset link
 */
export async function generateLink(data: GenerateLinkRequest): Promise<void> {
  await apiClient.post('/api/auth/generate-link/', data);
}

/**
 * The wire's `context.network.role` is a free string; the client type is the
 * UserLevel union (#1322). Anything that is not admin/manager/viewer becomes
 * `null`, which every consumer treats as viewer (least privilege) and reports.
 */
function narrowNetworkRole(user: AuthMeResponse): AuthMeResponse {
  const context = user.context;
  const network = context?.network ?? null;
  if (context === undefined || network === null) return user;
  const role: unknown = network.role;
  if (role === 'admin' || role === 'manager' || role === 'viewer') return user;
  return { ...user, context: { ...context, network: { ...network, role: null } } };
}

/**
 * Get current authenticated user.
 *
 * A 401 here goes through the global interceptor bounce: the session is over,
 * so the app leaves for `/login/signIn?reason=…`. Six in-app call sites rely on
 * that (session refetch, context switches, the profile hooks) — do NOT add the
 * exchange flag here. A call site that must survive its own 401 uses
 * `exchangeToken` below instead (W6).
 */
export async function getMe(authToken?: string): Promise<AuthMeResponse> {
  const headers = authToken ? { Authorization: `Token ${authToken}` } : undefined;
  const response = await apiClient.get<AuthMeResponse>('/api/auth/me/', { headers });
  return narrowNetworkRole(response.data);
}

/**
 * Canonical query options for the `['auth','me']` query.
 *
 * #479: every TanStack Query observer on `AUTH_ME_KEY` MUST spread these options
 * so the shared Query always carries the real `/api/auth/me/` fetch. In TanStack
 * Query v5, the last observer to call `setOptions` overwrites `query.options`
 * (including `queryFn`), so observers that install a different `queryFn` hijack
 * every `invalidateQueries`-driven refetch.
 */
export function authMeQueryOptions() {
  return { queryKey: AUTH_ME_KEY, queryFn: () => getMe() } as const;
}

/**
 * Exchange a freshly minted auth token for the user it belongs to, WITHOUT the
 * global 401 bounce (`skipAuthRedirect`).
 *
 * Who may call this: only the two explicit token-exchange sites — the `/sso`
 * page (a magic-link / OAuth arrival, `?auth_token=…`) and SignIn immediately
 * after a password success. Both hold a token the person has not been
 * signed in with yet, and both have an inline error surface of their own.
 *
 * Why: the interceptor's bounce sets `window.location.href`, which unloads the
 * page before the caller's catch path can render anything — the person sees a
 * blank login screen with no message (#268). Opting out makes the rejection a
 * plain promise rejection; the caller maps the server detail to copy with
 * `exchangeFailureCopy` and files the client report itself.
 *
 * Every OTHER caller keeps `getMe`.
 */
export async function exchangeToken(authToken: string): Promise<AuthMeResponse> {
  const response = await apiClient.get<AuthMeResponse>('/api/auth/me/', {
    headers: { Authorization: `Token ${authToken}` },
    skipAuthRedirect: true,
  });
  return narrowNetworkRole(response.data);
}

/**
 * Validate the session this device ALREADY holds (the stored token), WITHOUT
 * the global 401 bounce (`skipAuthRedirect`).
 *
 * Who may call this: only `/sso` after a FAILED arrival (#719). A re-tapped
 * mailed link that is expired or already used must not end the person's live
 * session: the caller asks whether the stored session still validates and, if
 * so, continues into the app silently. A 401 here is a plain rejection the
 * caller handles (it clears the dead token and shows today's failure) — a
 * bounce would unload `/sso` before that failure can render (#268).
 *
 * Every other caller keeps `getMe`.
 */
export async function validateStoredSession(): Promise<AuthMeResponse> {
  const response = await apiClient.get<AuthMeResponse>('/api/auth/me/', {
    skipAuthRedirect: true,
  });
  return narrowNetworkRole(response.data);
}

// ============================================
// Auth Link Exchange (vingapi#372)
// ============================================

/**
 * Stage response from `POST /api/auth/link/exchange/ {phase: 'stage'}`.
 *
 * The server creates an `auth_link_exchange` row and returns a short-lived
 * `exchange_id` for the complete step. The key is NOT verified at this stage.
 */
export interface AuthLinkStageResponse {
  exchangeId: string;
}

/**
 * Complete response from `POST /api/auth/link/exchange/ {phase: 'complete'}`.
 *
 * On success the server verified the magic link, resolved identity (#233),
 * minted a token, and returns `{auth_token, user}`. The SPA calls `signIn`
 * with the result.
 */
export interface AuthLinkCompleteResponse {
  authToken: string;
  user: User;
}

/**
 * Rejection envelope from the exchange: 4xx with
 * `{detail: {error: 'invalid_link', message, reset, trace}}`.
 *
 * Callers read the body on a rejected complete and map it to the login error
 * screen.
 */
export interface AuthLinkRejection {
  error: 'invalid_link';
  message: string;
  reset?: boolean;
  trace?: string;
}

/**
 * POST /api/auth/link/exchange/ — stage or complete a magic-link exchange.
 *
 * `skipAuthRedirect: true` because the caller owns its own UX and reporting
 * for any 4xx. Rate-limited at AUTH_SLOW on the server.
 */
export async function authLinkExchange(
  data:
    | { methodId: number; key: string; phase: 'stage' }
    | { methodId: number; key: string; phase: 'complete'; exchangeId: string },
): Promise<AuthLinkStageResponse | AuthLinkCompleteResponse> {
  const response = await apiClient.post<
    AuthLinkStageResponse | AuthLinkCompleteResponse
  >('/api/auth/link/exchange/', data, {
    skipAuthRedirect: true,
  });
  return response.data;
}

/**
 * Get app configuration
 * GET /api/config/
 *
 * Returns UI configuration: sort options, filters, page sizes, colors, validation rules.
 * This endpoint should be called once per session after authentication.
 * Response is identical for all users in an org and only changes on deployment.
 */
export async function getAppConfig(): Promise<NewAppConfig> {
  const response = await apiClient.get<NewAppConfig>('/api/config/');
  return response.data;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await apiClient.post('/api/auth/logout/');
}

/**
 * Change password for authenticated user
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await apiClient.post('/api/auth/password/', data);
}

/** Bound on the "Not Now" dismiss request only (#1606, Q-551): the dialog has already closed, the user is never held by it. */
export const DISMISS_PASSWORD_PROMPT_TIMEOUT_MS = 3000;

/**
 * End the "set a password" prompt for a user who closed the dialog without
 * setting one (#1606, vingapi#2112). 204 for any signed-in user, idempotent.
 *
 * `skipAuthRedirect`: a 401 here is a plain rejection the caller ignores, never
 * a sign-out. Callers swallow every failure; this is not a mutation and does not
 * route through the central error banner.
 */
export async function dismissPasswordPrompt(): Promise<void> {
  await apiClient.post('/api/auth/password/prompt/dismiss/', undefined, {
    timeout: DISMISS_PASSWORD_PROMPT_TIMEOUT_MS,
    skipAuthRedirect: true,
  });
}

/**
 * Get Google OAuth URL for SSO.
 *
 * Bug-hunt #79: `redirectPath` is validated against the same-origin
 * allowlist before being round-tripped through the OAuth provider — a
 * compromised query string can no longer carry an attacker host through
 * to the post-login redirect.
 */
export function getGoogleAuthUrl(redirectPath?: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  // vingapi registers Google under the bare `google` slug (not the legacy
  // python-social-auth `google-oauth2`) and reads the post-auth target from
  // `next`. This button is UI-controlled, so the slug change is safe.
  let url = `${baseUrl}/api/auth/social/login/google/`;
  if (redirectPath) {
    const safe = validateRedirect(redirectPath);
    url += `?next=${encodeURIComponent(safe)}`;
  }
  return url;
}

/**
 * Get ADP OAuth URL for SSO. See `getGoogleAuthUrl` for the redirect
 * validation rationale (bug-hunt #79).
 *
 * ADP's external app config registers the legacy `adp-oauth2` SSO + redirect
 * URLs, so the button stays on that slug; vingapi maps it to the unified ADP
 * provider while keeping the legacy redirect_uri so ADP's registration matches.
 * (vingapi shape uses `next`, not the legacy Django `redirect`.)
 */
export function getAdpAuthUrl(redirectPath?: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  let url = `${baseUrl}/api/auth/social/login/adp-oauth2/`;
  if (redirectPath) {
    const safe = validateRedirect(redirectPath);
    url += `?next=${encodeURIComponent(safe)}`;
  }
  return url;
}

/**
 * Get the per-network NOVI SSO login URL for a given network slug.
 *
 * NOVI is multi-tenant: the slug (the network's `identifier`, from
 * GET /api/branding/) selects which association's NOVI tenant authenticates the
 * user — `/api/auth/social/login/novi/{slug}/`. This is the new vingapi shape,
 * so the post-auth redirect rides the `next` query param (not the legacy Django
 * `redirect`). The redirect is validated against the same-origin allowlist
 * (bug-hunt #79), same as the Google/ADP helpers.
 */
export function getNoviAuthUrl(slug: string, redirectPath?: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  let url = `${baseUrl}/api/auth/social/login/novi/${encodeURIComponent(slug)}/`;
  if (redirectPath) {
    const safe = validateRedirect(redirectPath);
    url += `?next=${encodeURIComponent(safe)}`;
  }
  return url;
}

// Merge account functions
export interface MergeVerifyRequest {
  verificationMethod: string;
}

export interface MergeExecuteRequest {
  code: string;
}

/**
 * Response from the merge execute endpoint. The backend returns HTTP 200 with
 * this body even when the verification code is wrong or the merge pipeline
 * fails (`success: false`) — it does NOT raise a 4xx. Callers must branch on
 * `success`, not on the HTTP status.
 */
export interface VerifyCodeResponse {
  success: boolean;
  error?: string | null;
  mergeId?: number | null;
}

/**
 * 200 body of `POST /api/users/merge/{id}/verify/` (vingapi `MergeCodeSentResponse`,
 * #1666 S2). The same answer whether this press sent a code or fell inside the
 * 60 s cooldown of the same method (then nothing was sent and the code already
 * sent stays valid).
 */
export interface MergeCodeSentResponse {
  /** Seconds until another code can be sent to this method: 60 after a send, else what is left. */
  retryAfter: number;
}

/**
 * Send verification code for account merge.
 *
 * Rejects with 429 `verification_rate_limited` when a send budget is spent;
 * see `parseMergeSendRateLimit` in api/mergeSendRefusal.ts.
 */
export async function sendMergeVerification(
  suggestionId: number,
  data: MergeVerifyRequest
): Promise<MergeCodeSentResponse> {
  const response = await apiClient.post<MergeCodeSentResponse>(
    `/api/users/merge/${suggestionId}/verify/`,
    data
  );
  return response.data;
}

/**
 * Execute account merge with verification code
 */
export async function executeMerge(
  suggestionId: number,
  data: MergeExecuteRequest
): Promise<VerifyCodeResponse> {
  const response = await apiClient.post<VerifyCodeResponse>(
    `/api/users/merge/${suggestionId}/execute/`,
    data
  );
  return response.data;
}

/** Response from a successful claim initiation. */
export interface ClaimContactMethodResponse {
  mergeId: number;
  /**
   * Echoes back ONLY the method the claimant named. Restricting the code's
   * destination to the address they demonstrably already knew is what keeps
   * the 409 from disclosing anything new about the owner's account
   * (identity-plan.md §7).
   */
  sentTo: string;
  /**
   * Seconds until another code can go to this method (#1666 S2): 60 after a
   * send, or what is left when the claim fell inside the cooldown of a code
   * already sent there. Typed, not rendered.
   */
  retryAfter: number;
}

/**
 * Initiate a cross-user claim on a contact method another account has
 * verified — the affordance the `method_owned_by_another_account` 409 points
 * at (identity-plan.md §7).
 *
 * Creates (or reuses) a UserMerge for (you, the method's owner) and sends a
 * verification code to exactly the method named. Nothing about the other
 * account is disclosed and nothing changes until control is proven via
 * `executeMerge`, which runs the same verify-then-merge flow as any other
 * suggestion.
 *
 * Rejects with a 409 carrying `not_claimable` or `claim_rate_limited` — see
 * `parseClaimRefusal` in api/contactMethodClaim.ts.
 */
export async function claimContactMethod(
  data: { email: string } | { phone: string }
): Promise<ClaimContactMethodResponse> {
  // The client's interceptors snake-case the request and camel-case the
  // response, so `merge_id`/`sent_to` arrive already converted.
  const response = await apiClient.post<ClaimContactMethodResponse>(
    '/api/users/merge/claim/',
    data
  );
  return response.data;
}

/**
 * Ignore merge suggestion
 */
export async function ignoreMerge(suggestionId: number): Promise<void> {
  await apiClient.post(`/api/users/merge/${suggestionId}/ignore/`);
}

// ============================================
// Context Switching API Functions
// ============================================

// Note: a local PaginatedResponse<T> interface used to live here. It
// was removed in favor of the shared zod-based `paginatedResponse(...)`
// helper from `./schemas`, wired into getOrganizations + getNetworks
// as part of bug-hunt #11.

/**
 * Get organizations the user can switch to
 * GET /api/organizations/?search=...
 *
 * Returns organizations where the user has admin or manager level membership.
 * Used to populate the context switcher dialog.
 *
 * @param search - Optional search query to filter by name or identifier (server-side)
 */
// F14 (intent): tightened item schema to require id+name so malformed wire
// shapes reject loudly instead of silently propagating undefined fields to
// the UI. Extra fields are allowed (passthrough) for forward compatibility.
// The wire's FileRef (see @/types/file) — passthrough so a future field on the
// schema doesn't reject an otherwise-valid response.
const FileRefSchema = z
  .object({
    status: z.enum(['processing', 'ready', 'failed']),
    type: z.enum(['image', 'video', 'audio', 'document', 'other']),
    filename: z.string(),
  })
  .passthrough();

const OrgListItemSchema: z.ZodType<OrganizationListItem> = z
  .object({
    id: z.number(),
    name: z.string().nullable(),
    identifier: z.string(),
    logo: FileRefSchema.nullable(),
    level: z.enum(['admin', 'manager', 'viewer']),
  })
  .passthrough() as z.ZodType<OrganizationListItem>;
const OrgListSchema = paginatedResponse(OrgListItemSchema);

/** Backend list cap (`limit: int = Query(25, ge=1, le=100)`). */
const CONTEXT_LIST_PAGE_SIZE = 100;

/**
 * Fetch EVERY page of an offset-paginated list at the max page size, following
 * `next` until exhausted. The context switcher must show every org/network the
 * user belongs to — the backend default page (25) silently truncated users in
 * more than 25 orgs (QA: mobile-org-picker-missing-orgs).
 */
async function getAllPages<T>(
  path: string,
  schema: z.ZodType<{ next?: string | null; results: T[] }>,
  search?: string,
): Promise<T[]> {
  const results: T[] = [];
  let offset = 0;
  for (;;) {
    const params = new URLSearchParams();
    if (search?.trim()) params.set('search', search.trim());
    params.set('limit', String(CONTEXT_LIST_PAGE_SIZE));
    if (offset > 0) params.set('offset', String(offset));
    const response = await apiClient.get<unknown>(`${path}?${params}`);
    const page = parseResponse(schema, response.data);
    results.push(...page.results);
    if (!page.next || page.results.length === 0) return results;
    offset += page.results.length;
  }
}

export async function getOrganizations(search?: string): Promise<OrganizationListItem[]> {
  return getAllPages('/api/organizations/', OrgListSchema, search);
}

/**
 * Get networks the user can switch to
 * GET /api/networks/?search=...
 *
 * Returns networks where the user has membership (owner or member role).
 * Used to populate the context switcher dialog.
 *
 * @param search - Optional search query to filter by name or identifier (server-side)
 */
const NetworkListItemSchema: z.ZodType<NetworkListItem> = z
  .object({
    id: z.number(),
    name: z.string().nullable(),
    identifier: z.string(),
    // Backend contract is `str | None` ('admin' | 'manager' | 'viewer', or
    // 'admin' for staff). Don't re-tighten to an enum — that drift rejected
    // every real network for staff and broke the context switcher.
    role: z.string().nullable(),
    organizationCount: z.number(),
  })
  .passthrough() as z.ZodType<NetworkListItem>;
const NetworkListSchema = paginatedResponse(NetworkListItemSchema);

export async function getNetworks(search?: string): Promise<NetworkListItem[]> {
  // Same boundary validation as getOrganizations — bug-hunt #11.
  return getAllPages('/api/networks/', NetworkListSchema, search);
}

const MemberNetworkSchema: z.ZodType<MemberNetwork> = z
  .object({
    id: z.number(),
    name: z.string().nullable(),
  })
  .passthrough() as z.ZodType<MemberNetwork>;
const MemberNetworkListSchema = z.array(MemberNetworkSchema);

/**
 * GET /api/networks/member-of/ — the networks the CURRENT ORG context belongs
 * to. Powers the packet/checklist "Network" filter dropdown. Returns a plain
 * (non-paginated) array, [] in network context or for an org with no networks.
 */
export async function getMemberNetworks(): Promise<MemberNetwork[]> {
  const response = await apiClient.get<unknown>('/api/networks/member-of/');
  return parseResponse(MemberNetworkListSchema, response.data);
}

/**
 * The context-switch endpoint returns ONLY the new context, not a full
 * profile (backend `ContextSwitchResponse`). Typing it as `AuthMeResponse`
 * previously invited callers to write it straight into the `['auth','me']`
 * cache, wiping avatar + is_staff/is_superuser until a manual reload. Callers
 * must merge `.context` onto the existing user — see useSwitchContext.
 */
export interface ContextSwitchResponse {
  context: UserContext;
}

/**
 * Switch the user's active context
 * PUT /api/auth/context/
 *
 * Switches to a new organization or network context. Returns ONLY the new
 * context (see ContextSwitchResponse) — NOT the full user profile.
 *
 * @throws 400 - Invalid input (bad type value)
 * @throws 403 - User doesn't have access to the specified org/network
 */
export async function switchContext(data: ContextSwitchInput): Promise<ContextSwitchResponse> {
  const response = await apiClient.put<ContextSwitchResponse>('/api/auth/context/', data);
  return response.data;
}

